import { RotationSprite } from './RotationSprite';
import MainScene from 'src/app/Scenes/MainScene';
import { Player } from './Player';
import { DropItem } from './DropItem';
import { Inventory } from './UI/Inventory';
import BaseLevelScene from 'src/app/Scenes/BaseLevelScene';
import { KeyItem } from './Keys';
import { Preferences } from '@capacitor/preferences';

// This class will provide simple creatures for the game.
// They may include squirrels, bats, etc.
// Creatures are like RotationSprites, but they have additional traits
// 1. Animations
// 2. Health
// 3. Run/Attack
// They will  also derive their attributes from the
// name_to_attribute map and will be referenced by their frame name
export class Creature extends RotationSprite {
  health: integer;
  aggressive: boolean = false;
  speed: number = 2;
  _position: Phaser.Math.Vector2;
  currentRow: number = 0;
  currentCol: number = 0;
  previousPosition = new Phaser.Math.Vector2(0,0);
  previousRow: number = 0;
  previousCol: number = 0;
  allowedOnTile: boolean = true;
  onWater: boolean = false;
  target: Player = null;
  attackTimer = null;
  wanderTimer = null;
  playerScale: number;
  fadeOut: boolean = true;
  public inRange: boolean;
  allowRotation: boolean;
  token: string;
  canUpdate: boolean = true;
  deathMusic: string = null;

  constructor(data, bodies: MatterJS.BodiesFactory, aggressive: boolean, playerScale: number=1, allowRotation=true, token?: string, deathMusic?: string) {
    super(data);
    this.inRange = false;
    this.playerScale = playerScale;
    const centeredX = this.x + this.frame.width/2;
    const centeredY = this.y - this.frame.height/2;
    this._position = new Phaser.Math.Vector2(this.x, this.y);
    this.aggressive = aggressive;
    this.speed = this.speed*playerScale;
    this.allowRotation = allowRotation;
    this.token = token;
    this.deathMusic = deathMusic;

    // Create sensors and colliders for creatures
    const creatureCollider = bodies.circle(this.x, this.y, 20, {
      isSensor: false,
      label: 'creatureCollider',
    });
    const creatureSensor = bodies.circle(this.x, this.y, 80, {
      isSensor: true,
      label: 'creatureSensor',
    });
    // Combine sensors into a single body
    const Body = new Phaser.Physics.Matter.MatterPhysics(this.scene).body;
    const compoundBody = Body.create({
      parts: [creatureCollider, creatureSensor],
      frictionAir: 0.0,
      mass: 100,
    });
    this.setExistingBody(compoundBody);
    this.setScale(this.playerScale);
    this.setDepth(1);

    if(this.name === "ghost" && !Inventory.activeEnhancements.includes("clairvoyance")){
      this.setAlpha(0.25);
    }

    this.health = this.attributes.health;
    this.drops = this.attributes.drops;
  }

  // Static function to load in all animation data
  static preload(scene: Phaser.Scene) {
    // Load bat data
    scene.load.atlas(
      'bat',
      'assets/sprites/bat.png',
      'assets/sprites/bat_atlas.json'
    );
    scene.load.animation('bat_anims', 'assets/sprites/bat_anim.json');
    scene.load.audio('bat_sound', 'assets/audio/bat_01.mp3');

    // Load ghost data
    scene.load.atlas(
      'ghost',
      'assets/sprites/ghost.png',
      'assets/sprites/ghost_atlas.json'
    );
    scene.load.animation('ghost_anims', 'assets/sprites/ghost_anim.json');
    scene.load.audio('ghost_sound', 'assets/audio/aw01.mp3');

    // load bass data
    scene.load.atlas(
      'bass',
      'assets/sprites/bass.png',
      'assets/sprites/bass_atlas.json'
    );
    scene.load.animation('bass_anims', 'assets/sprites/bass_anim.json');

    // load gar data
    scene.load.atlas(
      'gar',
      'assets/sprites/gar.png',
      'assets/sprites/gar_atlas.json'
    );
    scene.load.animation('gar_anims', 'assets/sprites/gar_anim.json');

    // load squirrel data
    scene.load.atlas(
      'squirrel',
      'assets/sprites/squirrel.png',
      'assets/sprites/squirrel_atlas.json'
    );
    scene.load.animation('squirrel_anims', 'assets/sprites/squirrel_anim.json');
    scene.load.audio('squirrel_sound', 'assets/audio/mouse-2.mp3');

    // load raven data
    scene.load.atlas(
      'raven',
      'assets/sprites/raven.png',
      'assets/sprites/raven_atlas.json'
    );
    scene.load.animation('raven_anims', 'assets/sprites/raven_anim.json');

    // load gouhl data
    scene.load.atlas(
      'gouhl',
      'assets/sprites/gouhl.png',
      'assets/sprites/gouhl_atlas.json'
    );
    scene.load.animation('gouhl_anims', 'assets/sprites/gouhl_anim.json');
    scene.load.audio('gouhl_sound', 'assets/audio/aw01.mp3');


    // load octopus data
    scene.load.atlas(
      'octopus',
      'assets/sprites/octopus.png',
      'assets/sprites/octopus_atlas.json'
    );
    scene.load.animation('octopus_anims', 'assets/sprites/octopus_anim.json');
    scene.load.audio('octopus_sound', 'assets/audio/aw01.mp3');

    // load skeleton data
    scene.load.atlas(
      'skeleton',
      'assets/sprites/skeleton.png',
      'assets/sprites/skeleton_atlas.json'
    );
    scene.load.animation('skeleton_anims', 'assets/sprites/skeleton_anim.json');
    scene.load.audio('skeleton_sound', 'assets/audio/aw01.mp3');

    // Load One Last Pixie Stick song
    scene.load.audio('OneLastPixieStick', 'assets/music/One Last Pixie Stick.mp3');
  }

  static create(scene: MainScene){
    scene.sound.add('bat_sound');
    scene.sound.add('ghost_sound');
    scene.sound.add('squirrel_sound');
    scene.sound.add('gouhl_sound');
    scene.sound.add('OneLastPixieStick');
  }

  onDeath() {
    if(this.health == 0){

      // If randomly select an item that the creature can drop
      const dropIdx = Math.floor(Math.random() * this.drops.length);
      const drop = this.drops[dropIdx];

      // TODO: Consider removing this feature since we are using static keys instead.
      if(drop === "key"){
        const keyImage = "keys_" + Math.floor(Math.random()*3).toString();
        console.log("keyImage: ", keyImage);
        // TODO: Remove this and make keys static or assigned to a single creature.
        const key = (this.scene as BaseLevelScene).portals.find((portal) => {return portal.name !== "mainScene"});
        if(key){
          new KeyItem({ scene: this.scene, x: this.x, y: this.y, unlocks: [key.name] }, this.playerScale);
          // (this.scene as BaseLevelScene).portals.filter((portal) => {return portal.name !== "mainScene"});//[0].unlock(this.playerScale);
        }
      }
      else{
        new DropItem({ scene: this.scene, x: this.x, y: this.y, frame: drop }, this.playerScale);
      }
        
      // Play death music if specified
      if(this.deathMusic){
        console.log(`Playing death music: ${this.deathMusic}`);
        const music = this.scene.sound.add(this.deathMusic);
        music.play();
      }
      
      // Remove creature from scene
      clearInterval(this.attackTimer);
      this.destroy();

      if(this.token){
        if(!MainScene.tokens.includes(this.token)){
          console.log(`adding token: ${this.token}`);
          MainScene.tokens.push(this.token);
          Preferences.set({key: "tokens", value: JSON.stringify(MainScene.tokens)});
          // Store clientModifiedAt in Preferences
          Preferences.set({
            key: 'clientModifiedAt',
            value: new Date().toISOString(),
          });
        }
      }

    }
  }


  flash() {
    this.setTint(0xff0000);
    setTimeout(() => {
      this.setTint(0xff000);
    }, 250);
    setTimeout(() => {
      this.setTint(0xffffff);
    }, 250);
    setTimeout(() => {
      this.setTint(0xff000);
    }, 250);
    setTimeout(() => {
      this.setTint(0xffffff);
    }, 250);
  }

  // fade the sprite's alpha channel 0 to 1 and back
  loopFade(){
    if(this.alpha == 0){
      this.fadeOut = false;
    }
    if(this.alpha >= 0.25){
      this.fadeOut = true;
    }
    this.setAlpha(this.alpha + (this.fadeOut ? - 0.0025 : 0.0025));
  }

  hit() {
    this.health--;
    console.log(`Hitting:${this.name} Health:${this.health}`);
    let sound_key: string = `${this.name}_sound`;

    if(!this.dead){
      if(this.aggressive){
        this.chase(this.scene.player);
      }
      else{
        this.runAway(this.scene.player);
      }

      if (this.scene.sound.get(sound_key)) {
        this.scene.sound.play(sound_key);
      } 
    }
    if (this.dead) {
      this.onDeath();
    }
    this.flash();
  }



  get position() {
    this._position.set(this.x, this.y);
    return this._position;
  }

  get velocity() {
    return this.body.velocity;
  }

  get dead() {
    return this.health <= 0;
  }

  chase(target) {
    this.target = target;
    if (target.dead || this.dead) {
      clearInterval(this.attackTimer);
      clearInterval(this.wanderTimer);
      return;
    }
  }

  initiateAttack(){
    if (this.attackTimer == null && this.aggressive) {
      this.attackTimer = setInterval(this.attack.bind(this), 500);
    }
  }

  attack(){
    if(this.inRange && this.target.scene.scene.isActive()){
      if(Phaser.Math.Distance.Between(this.x, this.y, this.target.x, this.target.y) < 70*this.target.scale){
        this.target.hit();
      }
    }
  }

  runAway(target) {
    this.target = target;
  }

  wander(self: Creature){
    if(self.body){
      if(self.velocity.x**2 + self.velocity.y**2 > 0){
        self.setVelocity(0, 0);
      }
      else{
        let velocity = new Phaser.Math.Vector2(0, 0);
        velocity = Phaser.Math.RandomXY(velocity, self.speed/6);
        self.setVelocity(velocity.x, velocity.y);
      }
    }

  }

  setCreatureEnabled(enabled: boolean) {
    this.setActive(enabled);
    this.setVisible(enabled);
    if (this.body) {
      this.setSensor(!enabled); // Make body a sensor when disabled
      this.setIgnoreGravity(!enabled);
      this.setStatic(!enabled);
      if (!enabled) {
        this.setCollidesWith(0); // Disable all collisions
      } else {
        this.setCollidesWith(-1); // Enable collisions with all by default
      }
    }
    this.canUpdate = enabled;
  }

  update() {
    if (!this.active || !this.visible || this.canUpdate === false) {
      return;
    }
    if (this.dead) {
      this.destroy();
      return;
    }

    // if the creature is on water make it transparent
    if(this.onWater){
      this.setAlpha(0.5);
    }
    else{
      // if the creature is a ghost and you don't have clairvoyance
      // make the ghost fade in and out
      if(this.name === "ghost" && !Inventory.activeEnhancements.includes("clairvoyance")){
        this.loopFade();
      }

      // Otherwise the creature should be fully opaque
      else{
        this.setAlpha(1);
      }
    }
    if(this.allowedOnTile){
      if (this.target) {
        this.wanderTimer && clearInterval(this.wanderTimer);
        let direction = this.target.position.subtract(this.position);
        if (direction.length() > 5) {
          direction.normalize();
          if (this.aggressive) {
            this.setVelocity(direction.x * this.speed, direction.y * this.speed);
            if(Inventory.getItemQuantity("brown_rock") < 1 && this.scene.scene.key === 'mainScene'){
              this.aggressive = false;
              this.runAway(this.target);
            }
          } else {
            this.setVelocity(
              -direction.x * this.speed,
              -direction.y * this.speed
            );
          }
        }
      }
      //if not attacking creatures should move around randomly a bit
      else{
        if(this.wanderTimer == null){
          this.wanderTimer = setInterval(this.wander, Phaser.Math.Between(1000, 2000), this);
        }
      }
    }
    // reflect velocity if creature is not permitted to be on tile
    else{
      this.target = null;
      if(this.wanderTimer){
        clearInterval(this.wanderTimer);
        this.wanderTimer = null;
      }
      this.setPosition(this.previousPosition.x, this.previousPosition.y);
      if(this.previousRow !== this.currentRow){
        this.setVelocityY(-this.velocity.y);
      }
      if(this.previousCol !== this.currentCol){
        this.setVelocityX(-this.velocity.x);
      }
    }

    let direction: string;
    if (Math.abs(this.velocity.x) > Math.abs(this.velocity.y)) {
      if (this.velocity.x > 0) {
        direction = 'right';
      } else {
        direction = 'left';
      }
    } else {
      if (this.velocity.y > 0) {
        direction = 'down';
      } else {
        direction = 'up';
      }
    }

    const runKey = `${this.name}_run`;
    const attackKey = `${this.name}_attack`;
    const flyKey = `${this.name}_fly`;
    let animation_key: string = `${this.name}_${direction}`;
    if (this.anims.animationManager.get(animation_key)) {
      this.setRotation(0);
      this.anims.play(animation_key, true);
    } else {
      if (this.target && Math.sqrt(this.velocity.x ** 2 + this.velocity.y ** 2)) {
        let angle;
        if (this.aggressive) {
          angle = Phaser.Math.Angle.Between(
            this.x,
            this.y,
            this.scene.player.x,
            this.scene.player.y
          );
        } else {
          angle = Phaser.Math.Angle.Between(
            this.scene.player.x,
            this.scene.player.y,
            this.x,
            this.y
          );
        }

        if(this.allowRotation){
          this.setRotation(angle);
        }
        else{
          this.setRotation(0);
        }

        this.flipY = Math.abs(angle) > Math.PI / 2 && this.allowRotation;
        this.flipX = Math.abs(angle) > Math.PI / 2 && !this.allowRotation;

        if(this.inRange && this.aggressive && this.anims.animationManager.get(attackKey)){
          animation_key = attackKey;
        }
        else if (this.anims.animationManager.get(runKey)) {
          animation_key = runKey;
        }
        else{
          animation_key = flyKey;
        }

      } else {
        if(this.velocity.x**2 + this.velocity.y**2 == 0){
          animation_key = `${this.name}_idle`;
          this.setRotation(0);
          this.flipY = false;
          this.flipX = false;
        }
        else{
          let wanderAngle = Phaser.Math.Angle.Between(0, 0, this.velocity.x, this.velocity.y);
          this.flipY = Math.abs(wanderAngle) > Math.PI / 2 && this.allowRotation;
          this.flipX = Math.abs(wanderAngle) > Math.PI / 2 && !this.allowRotation;
          this.allowRotation && this.setRotation(wanderAngle);
          if (this.anims.animationManager.get(runKey)) {
            animation_key = runKey;
          }
          else{
            animation_key = flyKey;
          }
        }
      }
      this.anims.play(animation_key, true);
    }
  }
}
